import { ReactElement, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import Select, { SingleValue } from "react-select"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"

import {
  faArrowAltCircleRight,
  faBan,
  faEye,
  faCircleInfo,
  faThumbsUp,
  faTrash,
  faEyeSlash,
  faSquareCaretDown,
  IconDefinition,
  faRotateLeft,
  faFileExcel,
  faCloud,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Modal, Pagination, Loading, Tag, ContextMenu, ContextMenuItem, Button } from "@wfp/ui"
import { iconAddOutline, iconVisibilityOn, iconVisibilityOff } from "@wfp/icons"

import { MARKET_MODULE } from "constants/constants"

import { Report, ReportStatus, SimpleSelect } from "interfaces/dataInterfaces"
import { formatDate } from "utils/helpers"

import {
  useGetReportStatusQuery,
  useChangeReportStatusMutation,
  useDeleteReportMutation,
  useGetReportQuery,
} from "services/marketprices/reports"

import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import { ContentType } from "../../constants/markets"
import { ReportStatuses } from "../../constants/prices"
import ReportInfo from "../../components/Reports/ReportInfo"
import NewDataContent from "../../components/Markets/Dashboard/NewDataContent"

const tagType = {
  1: "warning",
  2: "info",
  3: "info",
  4: "success",
  5: "error",
  6: "info",
  7: "info",
  8: "error",
  9: "custom",
}
const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "reports",
}

const statusMap: Record<string, { status: number; message: string }> = {
  reject: { status: ReportStatuses.Rejected, message: "Are you sure you want to Reject the report?" },
  "mark-internal": {
    status: ReportStatuses.ApprovedInternal,
    message: "Are you sure you want to mark this report as Internal Use Only?",
  },
  "mark-public": { status: ReportStatuses.Approved, message: "Are you sure you want to mark this report as Public?" },
  unstage: { status: ReportStatuses.Pending, message: "Are you sure you want to unstage this report?" },
}

const ReportsPage = (): ReactElement => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const status = new URLSearchParams(search).get("status")

  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(20)
  const [showModal, setShowModal] = useState<boolean>(false)

  const [actionItem, setActionItem] = useState<{
    item: Report
    action: "delete" | "approve" | "reject" | "mark-internal" | "mark-public" | "unstage"
  } | null>(null)

  const currentAction = actionItem?.action ?? null
  const current = currentAction ? statusMap[currentAction] : undefined

  const [admCode, setAdmCode] = useState(0)
  const [reportStatus, setReportStatus] = useState<number>(status ? Number(status) : -1)
  const [infoModalData, setInfoModalData] = useState<null | Report>(null)

  const [deleteReport, { isLoading: isLoadingDelete }] = useDeleteReportMutation()
  const { data: statusList, isFetching: isLoadingReportStatus } = useGetReportStatusQuery()

  const { data: reports, isFetching: isLoadingReports } = useGetReportQuery({
    skip: page === 1 ? 0 : (page - 1) * pageSize,
    take: pageSize,
    adm0Code: admCode,
    status: reportStatus,
  })

  const [changeReportStatus, { isLoading: isLoadingStatus }] = useChangeReportStatusMutation()

  const RenderContextItem = ({
    onAction,
    feature,
    label,
    icon,
    color,
    disabled = false,
  }: {
    onAction: () => void
    feature: string
    label: string
    icon: IconDefinition
    color: string
    disabled?: boolean
  }): ReactElement | null => {
    if (disabled) return null
    return (
      <PermissionWrapper {...permissionsProps} feature={feature}>
        <ContextMenuItem className="p-1.5 px-2 text-left w-40 hover:bg-slate-200" onClick={onAction}>
          <div>
            <FontAwesomeIcon icon={icon} color={color} className="mr-1" />
            <span style={{ color }}>{label}</span>
          </div>
        </ContextMenuItem>
      </PermissionWrapper>
    )
  }

  const columns: Array<ColumnShape<Report>> = [
    {
      key: "info",
      width: 40,
      maxWidth: 100,
      align: Column.Alignment.CENTER,
      cellRenderer: ({ rowData }) => (
        <div className="cursor-pointer" onClick={() => setInfoModalData(rowData)} aria-hidden title="View Report Info">
          <FontAwesomeIcon icon={faCircleInfo} color="var(--interactive-01)" size="lg" />
        </div>
      ),
    },
    {
      key: "reportId",
      dataKey: "reportId",
      title: "ID",
      width: 130,
    },
    {
      key: "type",
      title: "",
      align: Column.Alignment.CENTER,
      width: 24,
      maxWidth: 24,
      flexGrow: 0,
      cellRenderer: ({ rowData }) =>
        rowData.sourceType === "excel" ? (
          <FontAwesomeIcon icon={faFileExcel} size="lg" color="green" title="Excel" className="h-4 w-4" />
        ) : (
          <FontAwesomeIcon icon={faCloud} size="lg" color="var(--interactive-01)" title="MoDA" className="h-4 w-4" />
        ),
    },
    {
      key: "reportStatusName",
      dataKey: "reportStatusName",
      title: "Status",
      width: 150,
      minWidth: 150,
      cellRenderer: ({ rowData }) => (
        <>
          <Tag
            type={tagType[rowData.reportStatusId] || "info"}
            className="font-bold"
            style={
              rowData.reportStatusId === ReportStatuses.ApprovedInternal
                ? { backgroundColor: "#6c757d", color: "#fff" }
                : undefined
            }
          >
            {rowData.reportStatusName}
          </Tag>

          {rowData.isUpdated && (
            <div className="relative flex w-2.5 h-2.5 ml-1" title="New Data Fetched">
              <div className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              <div className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#41B300] animate-pulse" />
            </div>
          )}
        </>
      ),
    },
    {
      key: "reportCreateDate",
      dataKey: "reportCreateDate",
      title: "Create Date",
      width: 350,
      cellRenderer: ({ rowData }) => (
        <div className="BaseTable__row-cell-text">{formatDate(rowData?.reportCreateDate, "DD-MMMM-YYYY HH:mm")}</div>
      ),
    },
    {
      key: "countryName",
      dataKey: "countryName",
      title: "Country",
      width: 350,
      flexGrow: 1,
    },
    {
      key: "uploadedByUserName",
      dataKey: "uploadedByUserName",
      title: "Create by",
      width: 300,
    },
    {
      key: "approvedByUserName",
      dataKey: "approvedByUserName",
      title: "Approved by",
      width: 300,
    },
    {
      key: "action",
      width: 60,
      align: Column.Alignment.CENTER,
      maxWidth: 60,
      flexGrow: 0,
      cellRenderer: ({ rowData }) => (
        <div
          aria-hidden
          className="d-flex align-items-center justify-end"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={
              <>
                <RenderContextItem
                  label="Details"
                  icon={faEye}
                  color="var(--interactive-01)"
                  feature="details"
                  disabled={
                    rowData.reportStatusId === ReportStatuses.Pending ||
                    rowData.reportStatusId === ReportStatuses.InProgress
                  }
                  onAction={() => navigate(`/market-prices/reports/${rowData.reportId}`)}
                />
                <RenderContextItem
                  label="Review"
                  icon={faEye}
                  color="var(--interactive-01)"
                  feature="review"
                  disabled={rowData.reportStatusId !== ReportStatuses.Pending}
                  onAction={() => navigate(`/market-prices/reports/${rowData.reportId}#review`)}
                />
                <RenderContextItem
                  label="Continue"
                  icon={faArrowAltCircleRight}
                  color="var(--support-02)"
                  feature="edit"
                  disabled={rowData.reportStatusId !== ReportStatuses.InProgress}
                  onAction={() => {
                    rowData.sourceType === "excel"
                      ? navigate(`/market-prices/data-upload/${rowData.reportId}`)
                      : navigate(`/market-prices/moda-data-upload/${rowData.reportId}`)
                  }}
                />
                <RenderContextItem
                  label="Approve"
                  icon={faThumbsUp}
                  color="var(--support-02)"
                  feature="approve"
                  disabled={rowData.reportStatusId !== ReportStatuses.Pending}
                  onAction={() => setActionItem({ item: rowData, action: "approve" })}
                />
                <RenderContextItem
                  label="Reject"
                  icon={faBan}
                  color="var(--support-01)"
                  feature="approve"
                  disabled={rowData.reportStatusId !== ReportStatuses.Pending}
                  onAction={() => setActionItem({ item: rowData, action: "reject" })}
                />
                <RenderContextItem
                  label="Mark as Internal"
                  icon={faEyeSlash}
                  color="var(--text-02)"
                  feature="approve"
                  disabled={rowData.reportStatusId !== ReportStatuses.Approved}
                  onAction={() => setActionItem({ item: rowData, action: "mark-internal" })}
                />
                <RenderContextItem
                  label="Mark as Public"
                  icon={faThumbsUp}
                  color="var(--support-02)"
                  feature="approve"
                  disabled={rowData.reportStatusId !== ReportStatuses.ApprovedInternal}
                  onAction={() => setActionItem({ item: rowData, action: "mark-public" })}
                />
                <RenderContextItem
                  label="Unstage"
                  icon={faRotateLeft}
                  color="var(--support-01)"
                  feature="approve"
                  disabled={
                    rowData.reportStatusId !== ReportStatuses.Approved &&
                    rowData.reportStatusId !== ReportStatuses.ApprovedInternal
                  }
                  onAction={() => setActionItem({ item: rowData, action: "unstage" })}
                />
                <RenderContextItem
                  label="Delete"
                  icon={faTrash}
                  color="var(--support-01)"
                  feature="edit"
                  disabled={rowData.reportStatusId !== ReportStatuses.InProgress}
                  onAction={() => setActionItem({ item: rowData, action: "delete" })}
                />
              </>
            }
          >
            <div className="cursor-pointer">
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  const handleDelete = async (id: number): Promise<void> => {
    await deleteReport(id).unwrap()
    setActionItem(null)
  }

  const getCountryData = (item?: SimpleSelect): void => {
    setAdmCode(item?.value ?? 0)
    setPage(1)
  }

  const getStatusData = (item: SingleValue<ReportStatus>): void => {
    const newStatus = item?.id ?? -1
    setReportStatus(newStatus)
    setPage(1)

    const params = new URLSearchParams(search)
    if (newStatus === -1) {
      params.delete("status")
    } else {
      params.set("status", newStatus.toString())
    }

    navigate(`/market-prices/reports?${params.toString()}`, { replace: true })
  }

  return (
    <>
      {(isLoadingDelete || isLoadingStatus) && <Loading small />}
      <HeaderFilters>
        <div className="flex w-3/4 items-center gap-2">
          <CountrySelector onSelect={value => getCountryData(value)} className="w-full" value={admCode} />

          <Select
            isLoading={isLoadingReportStatus}
            isSearchable
            className="w-full"
            placeholder="Select Report Status"
            getOptionLabel={opt => opt.name}
            getOptionValue={opt => opt.id?.toString()}
            options={statusList ?? []}
            onChange={item => getStatusData(item)}
            value={statusList?.find(item => item.id === reportStatus)}
            isClearable
          />
        </div>

        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button onClick={() => setShowModal(true)} icon={iconAddOutline} iconReverse>
            Add New Report
          </Button>
        </PermissionWrapper>
      </HeaderFilters>
      <div
        className={isLoadingReports ? "mt-2 d-flex align-center justify-center" : "mt-2"}
        style={{ height: "calc(100vh - 240px)", minHeight: "60vh" }}
      >
        {isLoadingReports ? (
          <Loading withOverlay={false} small />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={reports?.items ?? []}
                loading={isLoadingReports}
                width={width}
                height={height}
                rowKey="reportId"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>

      <Modal
        open={actionItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.reportId)}
        onRequestClose={() => setActionItem(null)}
        lazyLoad
      >
        Are you sure you want to delete the selected record?
      </Modal>

      <Modal
        open={actionItem?.action === "approve"}
        modalHeading="Report Approval"
        onRequestClose={() => setActionItem(null)}
        passiveModal
        lazyLoad
      >
        <div>
          <p className="mb-3">Select the visibility of the report</p>
          <div className="flex gap-4 justify-between">
            <Button
              onClick={() => {
                changeReportStatus({ id: actionItem?.item.reportId!, status: ReportStatuses.ApprovedInternal })
                setActionItem(null)
              }}
              icon={iconVisibilityOff}
              iconReverse
              kind="tertiary"
            >
              Internal
            </Button>

            <Button
              onClick={() => {
                changeReportStatus({ id: actionItem?.item.reportId!, status: ReportStatuses.Approved })
                setActionItem(null)
              }}
              icon={iconVisibilityOn}
              iconReverse
              kind="primary"
            >
              Public
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={infoModalData !== null}
        modalHeading="Report information"
        onRequestClose={() => setInfoModalData(null)}
        lazyLoad
        passiveModal
      >
        <ReportInfo data={infoModalData} />
      </Modal>

      <Modal
        open={!!current}
        modalHeading="Confirm status change"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        lazyLoad
        onRequestSubmit={async () => {
          if (!actionItem || !current) return
          await changeReportStatus({ id: actionItem.item.reportId, status: current.status })
          setActionItem(null)
        }}
        onRequestClose={() => setActionItem(null)}
      >
        {current?.message}
      </Modal>

      <Pagination
        totalItems={reports?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[20, 50, 100]}
        page={page}
        onChange={event => {
          const item = event as unknown as { page: number; pageSize: number }
          setPage(item.page)
          setPageSize(item.pageSize)
        }}
      />

      <Modal
        open={showModal}
        onRequestClose={() => setShowModal(false)}
        modalLabel="Add Price Data: Choose how to add new market price data"
        passiveModal
        lazyLoad
      >
        <NewDataContent moda={ContentType.moda} excel={ContentType.excel} />
      </Modal>
    </>
  )
}

export default ReportsPage
